import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Stack,
  Pagination,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalHospital as HospitalIcon,
} from "@mui/icons-material";
import { getUrgencyColor, getStatusColor, getFollowUpColor } from "../constants/referralOptions";

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function ReferralsTable({
  data = [],
  loading = false,
  total = 0,
  page = 1,
  pageSize = 15,
  onPageChange,
  onSelectReferral,
  onDeleteReferral,
}) {
  const totalPages = Math.ceil(total / pageSize);

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Member
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Specialist
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Reason
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Urgency
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Follow Up
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Date
              </TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Center
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "primary.main", color: "white" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading referrals...</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    No referrals found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((referral) => (
                <TableRow
                  key={referral.specialistReferralId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onSelectReferral?.(referral)}
                >
                  {/* Member Info */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {referral.patientName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {referral.cipId}
                    </Typography>
                  </TableCell>

                  {/* Specialist Info */}
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" fontWeight={500}>
                        {referral.specialistName || "—"}
                      </Typography>
                      {referral.specialistTypeName && (
                        <Chip
                          label={referral.specialistTypeName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      )}
                    </Stack>
                  </TableCell>

                  {/* Reason */}
                  <TableCell>
                    <Tooltip title={referral.reasonForReferral || ""}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {referral.reasonForReferral || "—"}
                      </Typography>
                    </Tooltip>
                    {referral.icd10Codes && (
                      <Chip
                        label={referral.icd10Codes}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem", mt: 0.5 }}
                      />
                    )}
                  </TableCell>

                  {/* Urgency */}
                  <TableCell>
                    <Chip
                      label={referral.urgencyName || "—"}
                      size="small"
                      color={getUrgencyColor(referral.urgencyName)}
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      label={referral.referralStatusName || "—"}
                      size="small"
                      color={getStatusColor(referral.referralStatusName)}
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>

                  {/* Follow Up */}
                  <TableCell>
                    <Chip
                      label={referral.followupStatusName || "—"}
                      size="small"
                      color={getFollowUpColor(referral.followupStatusName)}
                    />
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(referral.availityReferral)}
                    </Typography>
                  </TableCell>

                  {/* Center */}
                  <TableCell>
                    <Chip
                      icon={<HospitalIcon sx={{ fontSize: 14 }} />}
                      label={referral.medicalCenter || "—"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Edit Referral">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectReferral?.(referral);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!referral.hasLabPhlebotomy && (
                        <Tooltip title="Delete Referral">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteReferral?.(referral);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)} of {total}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange?.(newPage)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Paper>
  );
}
